// beziehungsAnzeige.ts — reine Logik für die Beziehungskarten der Anlage
// (User 26.09.2026): Zeilentexte der Listen, Netzwerk- und Familiendiagramm
// sowie der Organigramm-Graph aus den noch NICHT gespeicherten Einträgen.
//
// Die Einträge haben nur lokale ids (uid) — im Graphen bekommen sie negative
// Zahlen, bestehende Kontakte behalten ihre echte id. So lassen sich gespeicherte
// Netzwerke bestehender Firmen ohne Dubletten dazumischen.

import { RELATIONSHIP_FREE } from "../../utils/householdRole";
import { geschlechtAusAnrede } from "../../utils/anredeGeschlecht";
import type { VerantRole, VerantwortlichkeitValue } from "../verantwortlichkeit/VerantwortlichkeitInput";
import type { CompanyNetworkGraph, OrgGraphEdge, OrgGraphNode } from "../../services/companyNetworkService";
import { FIRMA_CONN_ROLES, canonicalFirmaConn } from "../../data/firmenverbindungen";
import type { ContactRef, FirmaConnItem, HouseholdItem, PersonFirmaItem } from "./types";
import type { ZeilenKontakt } from "../forms/BeziehungsTabelle";

/** Menschen fürs Unternehmen (Firmenanlage) — dieselbe Form wie `MenschEntry`. */
type MenschEintrag = { id: string; ref: ContactRef | null; verant: VerantwortlichkeitValue | null };

export const refName = (ref: ContactRef | null | undefined): string => {
  if (!ref) return "";
  if (ref.displayName?.trim()) return ref.displayName.trim();
  if (ref.type === "Firma") return (ref.lastName ?? "").trim();
  return [ref.titleLabel, ref.firstName, ref.lastName]
    .map((t) => (t ?? "").trim())
    .filter(Boolean)
    .join(" ");
};

/** „m" | „w" | „d" | null — aus `gender` oder, falls leer, aus der Anrede. */
export type GeschlechtKurz = "m" | "w" | "d" | null;
export const geschlechtKurz = (gender?: string | null, anrede?: string | null): GeschlechtKurz => {
  const g = (gender ?? "").trim() || geschlechtAusAnrede(anrede) || "";
  if (g === "Männlich") return "m";
  if (g === "Weiblich") return "w";
  if (g === "Divers") return "d";
  return null;
};

/** Alter in Jahren aus „YYYY-MM-DD" (auch mit Uhrzeit), sonst null. */
export const alterAus = (datum?: string | null, heute: Date = new Date()): number | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec((datum ?? "").trim());
  if (!m) return null;
  const [j, mo, t] = [Number(m[1]), Number(m[2]), Number(m[3])];
  let alter = heute.getFullYear() - j;
  if (heute.getMonth() + 1 < mo || (heute.getMonth() + 1 === mo && heute.getDate() < t)) alter--;
  return alter >= 0 && alter < 130 ? alter : null;
};

/** Was die andere Person für den neuen Kontakt ist („Ehefrau", Freitext …). */
export const beziehungsLabel = (it: Pick<HouseholdItem, "relationLabel" | "freeRole">): string =>
  it.relationLabel === RELATIONSHIP_FREE ? (it.freeRole ?? "").trim() : it.relationLabel;

export const rolleText = (r: VerantRole): string => {
  if (r.kind === "gf") return "Geschäftsführung";
  if (r.kind === "gesellschafter") {
    const anteil = r.share?.trim() ? ` (${r.share.trim()})` : "";
    return (r.istGeschaeftsfuehrer ? "Geschäftsführender Gesellschafter" : "Gesellschafter") + anteil;
  }
  return r.verantwortlichkeit + (r.share?.trim() ? ` (${r.share.trim()})` : "");
};

/** Quote einer Rolle („50 %") — die Geschäftsführung trägt keine. */
export const rollenAnteil = (r: VerantRole): string => ("share" in r ? (r.share ?? "").trim() : "");

/** Dieselbe Rolle ohne Quote — wenn die Quote eine eigene Spalte hat. */
export const ohneAnteil = (r: VerantRole): VerantRole => (r.kind === "gf" ? r : { ...r, share: undefined });

export const verantText = (v: VerantwortlichkeitValue | null | undefined): string => {
  if (!v) return "";
  const rollen = v.roles.map(rolleText).filter(Boolean);
  if (!rollen.length) return v.istMenschImUnternehmen ? "Mensch im Unternehmen" : "";
  return (v.isExtern ? "extern · " : "") + rollen.join(", ");
};

/** „Tochterfirma (50 %)" — die Rolle aus Sicht des neuen Unternehmens, ohne Klammererklärung. */
export const firmaConnText = (it: Pick<FirmaConnItem, "role" | "share">): string => {
  const label = (FIRMA_CONN_ROLES.find((r) => r.value === it.role)?.label ?? it.role).replace(/\s*\(.*\)$/, "");
  return label + (it.share?.trim() ? ` · ${it.share.trim()}` : "");
};

// ── Familiendiagramm ────────────────────────────────────────────────────────

/**
 * Generation der anderen Person relativ zum neuen Kontakt: +2 Großeltern,
 * +1 Eltern, 0 eigene Ebene, −1 Kinder, −2 Enkel. `null` = keine
 * Verwandtschaft ableitbar (Freitext wie „Patentante") → Zeile „Weitere".
 */
export const generationVon = (label: string): number | null => {
  const l = label.trim().toLowerCase();
  if (!l) return null;
  if (/^(ur)?gro(ß|ss)(vater|mutter|eltern)/.test(l)) return 2;
  if (/^enkel/.test(l)) return -2;
  // Onkel/Tante nur als ganzes Wort — sonst wäre die „Patentante" ein Elternteil.
  if (/(vater|mutter|eltern)$/.test(l) || /^(onkel|tante)$/.test(l)) return 1;
  if (/(sohn|tochter|kind|neffe|nichte)$/.test(l)) return -1;
  if (
    /(ehemann|ehefrau|partner|partnerin|bruder|schwester|schwager|schwägerin|cousin|cousine|geschwister)$/.test(l)
  )
    return 0;
  return null;
};

// ── Netzwerk ────────────────────────────────────────────────────────────────

/** Linienart im Netzwerkdiagramm. */
export type NetzArt = "haushalt" | "weitere" | "unternehmen" | "firma";

export type NetzKnoten = {
  id: string;
  name: string;
  typ: "Person" | "Firma";
  selbst?: boolean;
  geschlecht?: GeschlechtKurz;
  alter?: number | null;
  /** Verstorben → Kreuz vor dem Geschlechts-Symbol (User 28.09.2026). */
  verstorben?: boolean;
};
export type NetzKante = { von: string; zu: string; art: NetzArt; label: string };
export type Netz = { knoten: NetzKnoten[]; kanten: NetzKante[] };

/** Ein Kontakt als Knoten: bestehende über ihre id (dedupliziert), neue über die uid. */
const knotenId = (ref: ContactRef, eintragId: string) =>
  ref.contactId ? `k${ref.contactId}` : `n${eintragId}`;

export function anlageNetz(opts: {
  selbstName: string;
  selbstTyp: "Person" | "Firma";
  /** Anrede bzw. Geburtsdatum des neuen Kontakts (Formularfelder). */
  selbstAnrede?: string;
  selbstGeburtsdatum?: string;
  /** Der neue Kontakt selbst ist verstorben (Formularfeld „deceased"). */
  selbstVerstorben?: boolean;
  haushalt?: HouseholdItem[];
  weitere?: HouseholdItem[];
  personFirma?: PersonFirmaItem[];
  menschen?: MenschEintrag[];
  firmaConn?: FirmaConnItem[];
}): Netz {
  const knoten = new Map<string, NetzKnoten>();
  const kanten: NetzKante[] = [];
  knoten.set("self", {
    id: "self",
    name: opts.selbstName || "Neuer Kontakt",
    typ: opts.selbstTyp,
    selbst: true,
    geschlecht: opts.selbstTyp === "Person" ? geschlechtKurz(null, opts.selbstAnrede) : null,
    alter: opts.selbstTyp === "Person" ? alterAus(opts.selbstGeburtsdatum) : null,
    verstorben: opts.selbstTyp === "Person" && !!opts.selbstVerstorben,
  });
  const add = (ref: ContactRef | null, eintragId: string, art: NetzArt, label: string) => {
    if (!ref) return;
    const name = refName(ref);
    if (!name) return;
    const id = knotenId(ref, eintragId);
    if (!knoten.has(id)) {
      const person = ref.type !== "Firma";
      knoten.set(id, {
        id,
        name,
        typ: person ? "Person" : "Firma",
        geschlecht: person ? geschlechtKurz(ref.gender, ref.salutationLabel) : null,
        alter: person ? alterAus(ref.birthDate) : null,
        verstorben: person && !!ref.deceased,
      });
    }
    kanten.push({ von: "self", zu: id, art, label });
  };
  for (const it of opts.haushalt ?? []) add(it.ref, it.id, "haushalt", beziehungsLabel(it));
  for (const it of opts.weitere ?? []) add(it.ref, it.id, "weitere", beziehungsLabel(it));
  for (const it of opts.personFirma ?? []) add(it.ref, it.id, "unternehmen", verantText(it.verant));
  for (const it of opts.menschen ?? []) add(it.ref, it.id, "unternehmen", verantText(it.verant));
  for (const it of opts.firmaConn ?? []) add(it.ref, it.id, "firma", firmaConnText(it));
  return { knoten: [...knoten.values()], kanten };
}

// ── Organigramm ─────────────────────────────────────────────────────────────

/** id des neuen Kontakts im Organigramm-Graphen (es gibt ihn noch nicht). */
export const SELBST_ID = -1;

/**
 * Organigramm-Graph aus den Anlage-Einträgen, im Format des Backends
 * (`company-network-graph`): Rollenkanten src = Firma → dest = Person,
 * Tochterfirma src = Mutter → dest = Tochter. `OrganigrammPopup` filtert
 * danach selbst auf Organrollen und Struktur.
 */
export function anlageOrgGraph(opts: {
  selbstName: string;
  selbstTyp: "Person" | "Firma";
  personFirma?: PersonFirmaItem[];
  menschen?: MenschEintrag[];
  firmaConn?: FirmaConnItem[];
}): CompanyNetworkGraph {
  const nodes = new Map<number, OrgGraphNode>();
  const edges: OrgGraphEdge[] = [];
  let naechsteId = -2;
  const idFuer = new Map<string, number>();
  nodes.set(SELBST_ID, { id: SELBST_ID, name: opts.selbstName || "Neuer Kontakt", typ: opts.selbstTyp, isSelf: true });

  const knoten = (ref: ContactRef | null, eintragId: string): number | null => {
    if (!ref || !refName(ref)) return null;
    let id = ref.contactId ?? idFuer.get(eintragId);
    if (id == null) {
      id = naechsteId--;
      idFuer.set(eintragId, id);
    }
    if (!nodes.has(id)) nodes.set(id, { id, name: refName(ref), typ: ref.type === "Firma" ? "Firma" : "Person", isSelf: false });
    return id;
  };
  let kantenId = -1;
  const rollenKanten = (firma: number, person: number, v: VerantwortlichkeitValue | null) => {
    for (const r of v?.roles ?? []) {
      if (r.kind === "gf") {
        edges.push({ id: kantenId--, srcId: firma, destId: person, relationType: "Geschäftsführer", share: null, function: null });
      } else if (r.kind === "gesellschafter") {
        edges.push({
          id: kantenId--,
          srcId: firma,
          destId: person,
          relationType: "Gesellschafter",
          share: r.share?.trim() || null,
          function: r.istGeschaeftsfuehrer ? "geschäftsführend" : null,
        });
      } else {
        edges.push({
          id: kantenId--,
          srcId: firma,
          destId: person,
          relationType: "Mensch fürs Unternehmen",
          share: r.share?.trim() || null,
          function: r.verantwortlichkeit,
        });
      }
    }
  };

  // Person-Anlage: der neue Kontakt hat Rollen in Firmen.
  for (const it of opts.personFirma ?? []) {
    const f = knoten(it.ref, it.id);
    if (f != null) rollenKanten(f, SELBST_ID, it.verant);
  }
  // Firmen-Anlage: Menschen mit Rollen im neuen Unternehmen.
  for (const it of opts.menschen ?? []) {
    const p = knoten(it.ref, it.id);
    if (p != null) rollenKanten(SELBST_ID, p, it.verant);
  }
  // Firma↔Firma aus Sicht des neuen Unternehmens.
  for (const it of opts.firmaConn ?? []) {
    const f = knoten(it.ref, it.id);
    if (f == null) continue;
    // Dieselbe Richtung, die das Speichern schreibt (Mutter/Beteiligung umgedreht).
    const k = canonicalFirmaConn(it.role, SELBST_ID, f);
    edges.push({
      id: kantenId--,
      srcId: k.contactSrcId,
      destId: k.contactDestId,
      relationType: k.relationType,
      share: it.share?.trim() || null,
      function: null,
    });
  }
  return { nodes: [...nodes.values()], edges };
}

/** Bestehende Firmen der Einträge — deren gespeichertes Netzwerk wird nachgeladen. */
export const bestehendeFirmenIds = (refs: Array<ContactRef | null | undefined>): number[] => [
  ...new Set(
    refs
      .filter((r): r is ContactRef => !!r && r.type === "Firma" && !!r.contactId)
      .map((r) => r.contactId as number),
  ),
];

/**
 * Gespeicherte Netzwerke bestehender Firmen dazumischen. Knoten und Kanten
 * werden über ihre ids zusammengeführt; „isSelf" bleibt allein beim neuen
 * Kontakt, sonst stünden mehrere Mittelpunkte im Bild.
 *
 * `selbstId`: Gibt es den Kontakt schon (Akte im Bearbeiten-Modus), steht er in
 * den nachgeladenen Netzwerken seiner verbundenen Firmen ein zweites Mal — mit
 * seiner echten id neben dem synthetischen {@link SELBST_ID}. Dieser Knoten wird
 * auf `SELBST_ID` abgebildet, seine Kanten umgeschrieben und Dubletten, die
 * dadurch entstehen, fallen weg (Quip doppelt, 01.10.2026).
 */
export function graphenMischen(
  basis: CompanyNetworkGraph,
  weitere: CompanyNetworkGraph[],
  selbstId?: number | null,
): CompanyNetworkGraph {
  const abbilden = (id: number) => (selbstId != null && id === selbstId ? SELBST_ID : id);
  const nodes = new Map(basis.nodes.map((n) => [n.id, n]));
  const edges = new Map<number, OrgGraphEdge>();
  // Richtungs- und typbewusst: „A ist Mutter von B" und „B ist Mutter von A"
  // sind verschiedene Aussagen und bleiben beide stehen.
  const schluessel = (e: OrgGraphEdge) => `${e.srcId}|${e.destId}|${e.relationType}|${e.function ?? ""}`;
  const gesehen = new Set<string>();
  const nimm = (roh: OrgGraphEdge) => {
    const e = { ...roh, srcId: abbilden(roh.srcId), destId: abbilden(roh.destId) };
    if (e.srcId === e.destId) return;
    const k = schluessel(e);
    if (gesehen.has(k) || edges.has(e.id)) return;
    gesehen.add(k);
    edges.set(e.id, e);
  };
  for (const e of basis.edges) nimm(e);
  for (const g of weitere) {
    for (const n of g.nodes) {
      const id = abbilden(n.id);
      if (!nodes.has(id)) nodes.set(id, { ...n, id, isSelf: false });
    }
    for (const e of g.edges) nimm(e);
  }
  return { nodes: [...nodes.values()], edges: [...edges.values()] };
}

/**
 * Anzeige-Spalten einer Beziehungszeile (Kontaktmöglichkeit, Kontaktstatus) aus
 * dem Gegenkontakt — leer, solange er nur eingetippt und noch nicht gespeichert ist.
 */
export const zeilenAnzeigeVon = (
  ref: ContactRef | null | undefined,
): { kontakt?: ZeilenKontakt; status?: string } => {
  if (!ref?.contactId) return {};
  return {
    kontakt: {
      contactId: ref.contactId,
      name: refName(ref),
      typ: ref.type,
      telefon: ref.kommunikation ? ref.kommunikation.telefon : undefined,
      email: ref.kommunikation ? ref.kommunikation.email : undefined,
    },
    ...(ref.status ? { status: ref.status } : {}),
  };
};
